import {
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
} from '@angular/core';

/**
 * Fires `visible` once the host element enters the viewport while `enabled` is true, then
 * disconnects its observer. Re-arms automatically if `enabled` toggles false then true again
 * (e.g. the sentinel is moved to a new element for the next batch).
 *
 * Used to trigger auto-loading the next results batch once the user has scrolled roughly
 * halfway through the currently loaded results.
 */
@Directive({
  selector: '[commonIntersectionVisible]',
  standalone: true,
})
export class IntersectionVisibleDirective implements OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly ngZone = inject(NgZone);
  private observer: IntersectionObserver | null = null;

  enabled = input<boolean>(false, { alias: 'commonIntersectionVisible' });
  rootMargin = input<string>('0px');

  visible = output<void>();

  private readonly setupObserverEffect = effect(() => {
    const isEnabled = this.enabled();
    const rootMargin = this.rootMargin();
    this.teardown();

    if (!isEnabled || typeof IntersectionObserver === 'undefined') {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.teardown();
            this.ngZone.run(() => this.visible.emit());
          }
        },
        { rootMargin }
      );
      this.observer.observe(this.elementRef.nativeElement);
    });
  });

  ngOnDestroy(): void {
    this.teardown();
  }

  private teardown(): void {
    this.observer?.disconnect();
    this.observer = null;
  }
}
